import { useMemo, type Dispatch } from 'react';
import AgentApprovalsResource, { type AgentApproval } from 'Resources/AgentApprovals';
import {
	CRM_ACTIONS,
	PROSPECTING_ADD_ACTIONS,
	approvalToContactScrapeConfig,
	approvalToEmailDraft,
	approvalToMultiActionConfig,
	approvalToOfferDraft,
	approvalToProspectingAdd,
	approvalToWriteProposal,
	toProspectingAddAction
} from '../../Assistant/approvalAdapters';
import { approvalToToolProposal, TOOL_PROPOSAL_ACTIONS } from '../../Assistant/toolProposalAdapter';
import type {
	AssistantMessage,
	ContactScrapeConfig,
	EmailDraft,
	MultiActionConfig,
	OfferDraft,
	ProspectingAddConfig,
	ToolProposal,
	WriteProposal
} from '../../Assistant/types';
import type { AgentStreamHandlers } from './useAgentStream';
import type { MessagesAction } from './messagesReducer';

/**
 * The approval cards of a chat - what the agent proposes and waits on the rep for - in one place,
 * so every chat that renders MessageThread shows them and acts on them the same way. The AI
 * Workspace chat was the only one wiring them; Prospecting › Search drafted proposals the rep
 * could only see in the Inbox (WEB-15681).
 *
 * Three halves: the stream events that put a card in the thread as it is proposed, the updates
 * a card reports when the rep approves or rejects it, and rebuilding the cards from the stored
 * approvals when a conversation is loaded.
 *
 * A card resolves its approval server side (AgentApprovalsResource.approve / reject) and the
 * backend feeds the outcome back to the agent as a tool_result, so the agent continues on its
 * own. The updates below only move the card's local state - no synthetic follow-up message
 * (that used to double up with the backend resume).
 */

type ProposalStreamHandlers = Pick<
	AgentStreamHandlers,
	| 'onMultiAction'
	| 'onContactScrape'
	| 'onWriteProposal'
	| 'onEmailDraft'
	| 'onOfferDraft'
	| 'onToolProposal'
	| 'onProspectingAdd'
>;

/** The stream handlers that put a proposal card in the thread, tagged with the turn's run. */
export function proposalStreamHandlers(
	dispatch: Dispatch<MessagesAction>,
	streamRunId: string | null
): ProposalStreamHandlers {
	const metadata = streamRunId ? { runId: streamRunId } : undefined;
	const appendCard = (card: Partial<AssistantMessage>) =>
		dispatch({ type: 'append', message: { role: 'assistant', content: '', metadata, ...card } });

	return {
		onMultiAction: config => appendCard({ multiAction: config as MultiActionConfig }),
		onContactScrape: config => appendCard({ contactScrape: config as ContactScrapeConfig }),
		onWriteProposal: proposal => {
			const rawApprovalId = (proposal as { approvalId?: number | null }).approvalId;
			dispatch({
				type: 'addWriteProposal',
				proposal: {
					entity: String(proposal.entity ?? ''),
					action: proposal.action as WriteProposal['action'],
					data: (proposal.data as Record<string, unknown>) ?? {},
					status: 'pending',
					approvalId: typeof rawApprovalId === 'number' ? rawApprovalId : null,
					matchingContacts:
						typeof proposal.matchingContacts === 'number' ? proposal.matchingContacts : undefined,
					entityDisplayName:
						typeof proposal.entityDisplayName === 'string' ? proposal.entityDisplayName : undefined,
					mergeDisplayName:
						typeof proposal.mergeDisplayName === 'string' ? proposal.mergeDisplayName : undefined
				},
				runId: streamRunId
			});
		},
		onEmailDraft: draft =>
			appendCard({
				emailDraft: {
					contactIds: draft.contactIds,
					subject: draft.subject,
					body: draft.body,
					ccContactIds: draft.ccContactIds,
					clientId: draft.clientId,
					attachments: draft.attachments,
					status: 'pending',
					approvalId: draft.approvalId ?? null
				}
			}),
		onOfferDraft: draft =>
			appendCard({
				offerDraft: {
					clientId: draft.clientId,
					contactId: draft.contactId,
					products: draft.products,
					currency: draft.currency,
					validUntil: draft.validUntil,
					description: draft.description,
					notes: draft.notes,
					status: 'pending',
					approvalId: draft.approvalId ?? null
				}
			}),
		onToolProposal: proposal => appendCard({ toolProposal: { ...proposal, status: 'pending' } }),
		onProspectingAdd: draft =>
			appendCard({
				prospectingAdd: {
					id: `pa_approval_${draft.approvalId}`,
					action: toProspectingAddAction(draft.action),
					companyName: draft.companyName,
					orgNumber: draft.orgNumber,
					contacts: draft.contacts,
					companyCount: draft.companyCount,
					campaignName: draft.campaignName,
					credits: draft.credits,
					sample: draft.sample,
					accountManagerName: draft.accountManagerName,
					country: draft.country,
					status: 'pending',
					approvalId: draft.approvalId
				}
			})
	};
}

/** What a card reports back when the rep acts on it, as MessageThread's props take it. */
export function useProposalCardHandlers(dispatch: Dispatch<MessagesAction>) {
	return useMemo(
		() => ({
			onMultiActionUpdate: (id: string, updates: Partial<MultiActionConfig>) =>
				dispatch({ type: 'updateMultiAction', id, updates }),
			onContactScrapeUpdate: (id: string, updates: Partial<ContactScrapeConfig>) =>
				dispatch({ type: 'updateContactScrape', id, updates }),
			onWriteProposalUpdate: (index: number, update: Partial<WriteProposal>) =>
				dispatch({ type: 'updateWriteProposal', index, update }),
			onWriteProposalGroupItemUpdate: (messageIndex: number, itemIndex: number, update: Partial<WriteProposal>) =>
				dispatch({ type: 'updateWriteProposalGroupItem', messageIndex, itemIndex, update }),
			onApprovalsGroupEmailItemUpdate: (messageIndex: number, itemIndex: number, update: Partial<EmailDraft>) =>
				dispatch({ type: 'updateWriteProposalGroupEmailItem', messageIndex, itemIndex, update }),
			onEmailDraftUpdate: (index: number, update: Partial<EmailDraft>) =>
				dispatch({ type: 'updateEmailDraft', index, update }),
			onOfferDraftUpdate: (index: number, update: Partial<OfferDraft>) =>
				dispatch({ type: 'updateOfferDraft', index, update }),
			onToolProposalUpdate: (index: number, update: Partial<ToolProposal>) =>
				dispatch({ type: 'updateToolProposal', index, update }),
			onProspectingAddUpdate: (index: number, update: Partial<ProspectingAddConfig>) =>
				dispatch({ type: 'updateProspectingAdd', index, update })
		}),
		[dispatch]
	);
}

/** The approvals a stored message proposed (`metadata.approvalIds`). */
export function readApprovalIds(rawMetadata: unknown): number[] {
	try {
		const meta = typeof rawMetadata === 'string' ? JSON.parse(rawMetadata) : rawMetadata;
		const ids = (meta as { approvalIds?: unknown } | null)?.approvalIds;
		return Array.isArray(ids) ? ids.filter((n): n is number => typeof n === 'number') : [];
	} catch {
		// Unparseable metadata proposed nothing.
		return [];
	}
}

/**
 * Every approval the messages proposed, by id, in one request. A failed fetch only costs the
 * cards on this load - the transcript itself still renders, and the Inbox still holds them.
 */
export async function fetchApprovalsById(perMessageApprovalIds: number[][]): Promise<Map<number, AgentApproval>> {
	const ids = Array.from(new Set(perMessageApprovalIds.flat()));
	const byId = new Map<number, AgentApproval>();
	if (!ids.length) return byId;
	try {
		const fetched = await AgentApprovalsResource.getMany(ids);
		fetched.forEach(a => byId.set(a.id, a));
	} catch (err) {
		console.warn('[proposalWiring] could not load the approvals for this conversation', err);
	}
	return byId;
}

/**
 * What a card after a turn takes of the turn's metadata: only what files it under the same run.
 * The rest renders per message (researched companies, delegations, a delegation's drafts), so
 * copying it would repeat that under every card. No runMarker either: the card joins the turn's
 * run instead of opening a new one.
 */
function runLinkOf(metadata: AssistantMessage['metadata']): Record<string, unknown> | null {
	const meta = (metadata ?? {}) as Record<string, unknown>;
	const link = Object.fromEntries(
		(['runId', 'runTimestamp', 'agentRunId'] as const).filter(key => meta[key] != null).map(key => [key, meta[key]])
	);
	return Object.keys(link).length ? link : null;
}

/**
 * A stored message with the cards for the approvals it proposed. Writes and emails go into a
 * single "Proposals to review" group on the message itself (a post-meeting turn's two task drafts
 * and one email show under one header); a bulk action and a contact scrape are self-contained
 * editors on the message; offers, gated tool calls and prospecting drafts are a card each, after it.
 */
export function withApprovalCards(base: AssistantMessage, approvals: AgentApproval[]): AssistantMessage[] {
	if (!approvals.length) return [base];
	const runLink = runLinkOf(base.metadata);
	const extra = (card: Partial<AssistantMessage>): AssistantMessage => ({
		role: 'assistant',
		content: '',
		regDate: base.regDate,
		...(runLink ? { metadata: runLink } : {}),
		...card
	});
	const of = (match: (action: string) => boolean) => approvals.filter(a => match(a.action));

	const writes = of(action => CRM_ACTIONS.has(action));
	const emails = of(action => action === 'sendEmail');
	const multi = of(action => action === 'multiAction')[0];
	const scrape = of(action => action === 'scrapeContacts')[0];

	const message: AssistantMessage = { ...base };
	if (multi) message.multiAction = approvalToMultiActionConfig(multi);
	if (scrape) message.contactScrape = approvalToContactScrapeConfig(scrape);
	if (writes.length || emails.length) {
		message.writeProposalGroup = {
			items: [
				...writes.map(a => ({ kind: 'write' as const, proposal: approvalToWriteProposal(a) })),
				...emails.map(a => ({ kind: 'email' as const, draft: approvalToEmailDraft(a) }))
			]
		};
	}
	return [
		message,
		...of(action => action === 'draftOffer').map(a => extra({ offerDraft: approvalToOfferDraft(a) })),
		...of(action => TOOL_PROPOSAL_ACTIONS.has(action)).map(a => extra({ toolProposal: approvalToToolProposal(a) })),
		...of(action => PROSPECTING_ADD_ACTIONS.has(action)).map(a =>
			extra({ prospectingAdd: approvalToProspectingAdd(a) })
		)
	];
}
